/**
 * DotGridBackground
 * Creates a plane mesh imperatively, adds it to the camera so it lives in
 * camera-local space (never scales with scene zoom), and positions it at
 * DIST units in front of the lens so scene geometry renders in front.
 * texture.repeat updates every frame so dots stay at a fixed screen-pixel size.
 */

import { useEffect } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'

const DIST = 15          // units in front of camera — gateway is ~1.8 units away so this sits behind it
const DOT_SPACING = 30  // desired spacing between dots in screen pixels

function createDotTexture() {
  // 150×150 tile — 5×5 grid of dots at 30px spacing
  // Corners of the tile (0,0)(120,0)(0,120)(120,120) = 50% opacity
  // All other 21 dots = 20% opacity
  const canvas = document.createElement('canvas')
  canvas.width = 150
  canvas.height = 150
  const ctx = canvas.getContext('2d')
  ctx.clearRect(0, 0, 150, 150)

  // Only (0,0) is the accent dot — when the tile repeats this lands exactly
  // every 5th dot (every 150px = 5×30px) in both axes. Marking other tile
  // corners creates clusters of bright dots at seams.
  const corners = new Set(['0,0'])

  for (let x = 0; x < 150; x += 30) {
    for (let y = 0; y < 150; y += 30) {
      const isCorner = corners.has(`${x},${y}`)
      ctx.fillStyle = isCorner
        ? 'rgba(238, 58, 23, 0.3)'
        : 'rgba(238, 58, 23, 0.1)'
      ctx.beginPath()
      ctx.arc(x + 0.5, y + 0.5, isCorner ? 1.5 : 1, 0, Math.PI * 2)
      ctx.fill()
    }
  }

  const tex = new THREE.CanvasTexture(canvas)
  tex.wrapS = THREE.RepeatWrapping
  tex.wrapT = THREE.RepeatWrapping
  return tex
}

export function DotGridBackground({ visible }) {
  const { camera, size } = useThree()

  useEffect(() => {
    const texture = createDotTexture()
    const geo = new THREE.PlaneGeometry(1, 1)
    const mat = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      opacity: 1,
    })
    const mesh = new THREE.Mesh(geo, mat)
    mesh.renderOrder = -1
    mesh.position.set(0, 0, -DIST)
    mesh.userData.isDotGrid = true
    camera.add(mesh)

    return () => {
      camera.remove(mesh)
      geo.dispose()
      mat.dispose()
      texture.dispose()
    }
  }, [camera])

  // Every frame: resize plane to fill viewport and keep dot spacing fixed
  useFrame(() => {
    const mesh = camera.children.find(c => c.userData.isDotGrid)
    if (!mesh) return

    mesh.visible = visible

    const fovRad = (camera.fov * Math.PI) / 180
    const h = 2 * DIST * Math.tan(fovRad / 2)
    const w = h * camera.aspect
    mesh.scale.set(w, h, 1)

    // tile is 150px wide with 5 dots — repeat = screenPixels / tilePixelSize
    const mat = mesh.material
    mat.map.repeat.set(size.width / 150, size.height / 150)
  })

  return null
}

export default DotGridBackground
